import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { AppConst } from 'constants/appConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useRef, useState, type JSX } from 'react';
import ReactModal from 'react-modal';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { CompanyImageSearchItemType } from 'types/api/companyImageSearchItemType';
import { formatTimeToDate, getContentImageUrl, getImageType, uploadS3 } from 'utils/app';

/**
 * 画像一括生成モーダル
 */

interface ImageItem {
  key: string;
  name: string;
  title: string;
  description: string;
  imageKey?: string; // 既存の画像キー（オプショナル）
}

interface PromptSample {
  title: string;
  prompt: string;
  withReferenceImage: boolean;
}

interface GeneratedImage {
  key: string;
  imageKey: string;
  url: string;
}

export default function ModalCompanyImageGenerator(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  imageItems: ImageItem[];
  promptSamples: PromptSample[];
  onComplete: (generatedImages: { key: string; imageKey: string }[]) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState, setAcctState] = useAtom(AcctState);

  const { noticeMessageFailure, noticeMessageSuccess } = useAppState();
  const { postCompanyImageSearch, postCompanyImageGenerator, postCompanyImageRegister } =
    useApiState();

  const inputFileRef = useRef(null);

  const [initialized, setInitialized] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [processing, setProcessing] = useState(false);

  // プロンプト関連
  const [prompt, setPrompt] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [editedPrompts, setEditedPrompts] = useState<{ [key: string]: string }>({});
  const [enableReferenceImage, setEnableReferenceImage] = useState(true);

  // 参考画像関連
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [referenceImage, setReferenceImage] = useState<CompanyImageSearchItemType | null>(null);
  const [companyImagesState, setCompanyImagesState] = useState<CompanyImageSearchItemType[]>([]);
  const [keywordState, setKeywordState] = useState('');

  // 生成された画像
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);

  // 個別画像の生成対象選択
  const [selectedImageKeys, setSelectedImageKeys] = useState<Set<string>>(new Set());

  // エラーメッセージ
  const [errorMessage, setErrorMessage] = useState('');

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * モーダルを開いた後の処理
   */
  const onAfterOpen = async () => {
    try {
      setInitialized(true);
      // カンパニー画像検索APIの呼び出し
      const response = await postCompanyImageSearch({
        companyId: acctState.company.id,
      });
      setCompanyImagesState(response.images);

      // デフォルトで最初のサンプルを選択
      if (props.promptSamples.length > 0) {
        const firstSample = props.promptSamples[0];
        setPrompt(firstSample.prompt);
        setSelectedSample(firstSample.prompt);
        setOriginalPrompt(firstSample.prompt);
        setEnableReferenceImage(firstSample.withReferenceImage);
      }

      // デフォルトで生成可能な画像を選択状態にする
      const defaultSelectedKeys = new Set<string>();
      props.imageItems.forEach(item => {
        // タイトルがあり、既存画像がない画像のみをデフォルト選択
        if (item.title && item.title.trim() && !item.imageKey) {
          defaultSelectedKeys.add(item.key);
        }
      });
      setSelectedImageKeys(defaultSelectedKeys);
    } finally {
      setInitialized(false);
    }
  };

  /**
   * モーダルを閉じた後の処理
   */
  const onClose = () => {
    props.setOpen(false);
    _initState();
  };

  /**
   * サンプル選択時の処理
   */
  const onSelectSample = (samplePrompt: string) => {
    // 現在のプロンプトが編集されている場合、編集内容を保存
    if (selectedSample && prompt !== selectedSample) {
      setEditedPrompts(prev => ({
        ...prev,
        [selectedSample]: prompt,
      }));
    }

    // 選択されたサンプルを見つける
    const selectedSampleObj = props.promptSamples.find(sample => sample.prompt === samplePrompt);

    if (selectedSampleObj) {
      // 以前に編集された内容があれば、それを復元。なければ元のプロンプトを設定
      const restoredPrompt = editedPrompts[samplePrompt] || samplePrompt;
      setPrompt(restoredPrompt);
      setOriginalPrompt(samplePrompt);
      setEnableReferenceImage(selectedSampleObj.withReferenceImage);
    } else {
      setPrompt(samplePrompt);
      setOriginalPrompt(samplePrompt);
      setEnableReferenceImage(true);
    }

    setSelectedSample(samplePrompt);
  };

  /**
   * プロンプトをデフォルトに戻す
   */
  const onResetToDefault = () => {
    if (originalPrompt) {
      setPrompt(originalPrompt);
      // 編集履歴からも削除
      setEditedPrompts(prev => {
        const newEdited = { ...prev };
        delete newEdited[selectedSample];
        return newEdited;
      });
    }
  };

  /**
   * 参考画像ギャラリーを開く
   */
  const onOpenImageGallery = () => {
    setShowImageGallery(true);
  };

  /**
   * 参考画像を選択
   */
  const onSelectReferenceImage = (image: CompanyImageSearchItemType) => {
    setReferenceImage(image);
    setShowImageGallery(false);
  };

  /**
   * 参考画像をクリア
   */
  const onClearReferenceImage = () => {
    setReferenceImage(null);
  };

  /**
   * ファイルアップロードボタン押下時の処理
   */
  const onOpenFileUploader = () => {
    inputFileRef.current.click();
  };

  /**
   * ファイル選択ダイアログで選択された画像を読み取ります。
   */
  const onImgFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // ファイルの取得
      const file = e.target.files[0];

      if (AppConst.imageTypes.includes(file.type)) {
        // ファイルの読み込み
        const reader = new FileReader();

        reader.addEventListener('load', () => {
          const image = new Image();
          image.addEventListener('load', async () => {
            try {
              // ローディングの表示
              setProcessing(true);

              // ファイル拡張子を取得
              const extension = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2);

              // ファイルをS3に送信
              const uplodKey = await uploadS3(file, extension);

              // カンパニー画像登録APIの呼び出し;
              const response = await postCompanyImageRegister({
                companyId: acctState.company.id,
                name: file.name,
                imageWidth: image.width,
                imageHeight: image.height,
                imageSize: file.size,
                uploadKey: uplodKey,
              });

              // 新しい画像オブジェクトを作成
              const newImage: CompanyImageSearchItemType = {
                id: response.id,
                name: file.name,
                imageKey: response.imageKey,
                imageWidth: image.width,
                imageHeight: image.height,
                imageSize: file.size,
                createdAt: new Date().getTime(),
              };

              // 画像一覧に追加
              setCompanyImagesState(prevState => [newImage, ...prevState]);

              // アップロードした画像を自動選択
              setReferenceImage(newImage);

              noticeMessageSuccess('画像をアップロードしました。');
            } catch {
              noticeMessageFailure('画像のアップロードに失敗しました。');
            } finally {
              setProcessing(false);
            }
          });
          image.src = reader.result as string;
        });
        reader.readAsDataURL(e.target.files[0]);
      } else {
        noticeMessageFailure('JPEG、PNG、GIFファイルのみアップロード可能です。');
      }
      // 同じファイルを何度も指定できるように値を初期化
      e.target.value = '';
    }
  };

  /**
   * 画像生成開始
   */
  const onGenerateImages = async () => {
    setErrorMessage(''); // エラーメッセージをクリア

    if (!prompt.trim()) {
      setErrorMessage('プロンプトを入力してください。');
      return;
    }

    if (enableReferenceImage && !referenceImage) {
      setErrorMessage('参考画像を選択してください。');
      return;
    }

    // 選択された画像のみを生成対象とする
    const targetItems = props.imageItems.filter(item => selectedImageKeys.has(item.key));

    if (targetItems.length === 0) {
      setErrorMessage('生成対象の画像を選択してください。');
      return;
    }

    setGenerating(true);
    try {
      // 実際のAPI呼び出し
      const response = await postCompanyImageGenerator({
        companyId: acctState.company.id,
        sharedPrompt: prompt.trim(),
        imageKey: referenceImage?.imageKey || null,
        imageDescriptions: targetItems.map(item => ({
          key: item.key,
          name: item.name,
          title: item.title,
          description: item.description,
        })),
        saveTemporarily: false,
      });

      // 生成された画像を状態に設定
      const generatedImages = response.images.map((image, index) => ({
        key: targetItems[index]?.key || `generated_${index}`,
        imageKey: image.imageKey,
        url: getContentImageUrl(image.imageKey),
      }));

      setGeneratedImages(generatedImages);

      // AI画像生成数の更新
      setAcctState(prev => ({
        ...prev,
        company: {
          ...prev.company,
          aiImageUsageCount: response.aiImageUsageCount,
        },
      }));

      noticeMessageSuccess('画像の生成が完了しました。');
    } catch {
      noticeMessageFailure('画像の生成に失敗しました。');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * 個別画像の選択状態を切り替え
   */
  const onToggleImageSelection = (imageKey: string) => {
    setSelectedImageKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageKey)) {
        newSet.delete(imageKey);
      } else {
        newSet.add(imageKey);
      }
      return newSet;
    });
  };

  /**
   * 全ての画像を選択/選択解除
   */
  const onToggleAllImages = () => {
    // タイトルがある画像のみを対象とする
    const selectableKeys = props.imageItems
      .filter(item => item.title && item.title.trim())
      .map(item => item.key);

    const allSelected = selectableKeys.every(key => selectedImageKeys.has(key));

    if (allSelected) {
      setSelectedImageKeys(new Set());
    } else {
      setSelectedImageKeys(new Set(selectableKeys));
    }
  };

  /**
   * 生成完了ボタン押下時の処理
   */
  const onComplete = () => {
    const result = generatedImages.map(img => ({
      key: img.key,
      imageKey: img.imageKey,
    }));
    props.onComplete(result);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = () => {
    setPrompt('');
    setSelectedSample('');
    setOriginalPrompt('');
    setEditedPrompts({});
    setEnableReferenceImage(true);
    setReferenceImage(null);
    setGeneratedImages([]);
    setShowImageGallery(false);
    setKeywordState('');
    setSelectedImageKeys(new Set());
    setErrorMessage('');
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={initialized || generating || processing} />

      {/* 隠しファイル入力 */}
      <input
        type="file"
        ref={inputFileRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={onImgFileInput}
      />

      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        onRequestClose={onClose}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '1000px',
            width: '95vw',
            maxHeight: '100vh',
            overflowY: 'auto',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        <div className="modal modal-content rounded-4 modal-media">
          <div className="modal-header">
            <h4 className="modal-title">AIで画像を一括生成</h4>
          </div>
          <div className="modal-body">
            {/* プロンプト設定セクション */}
            <div className="section-content mb-3">
              {/* <h5 className="section-title">プロンプト設定</h5> */}

              <div className="row g-3">
                {/* 左カラム：プロンプト設定 */}
                <div className="col-md-9">
                  {/* プロンプト */}
                  <div className="form-control-container mb-3">
                    <label className="form-label mb-2">プロンプト</label>

                    {/* セレクトボックス */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="flex-1 me-2">
                        <select
                          className="form-select form-select-sm"
                          value={selectedSample}
                          onChange={e => onSelectSample(e.target.value)}
                          style={{
                            backgroundPosition: 'right 0.5rem center',
                            backgroundSize: '12px 9px',
                          }}
                        >
                          {props.promptSamples.map((sample, index) => (
                            <option key={index} value={sample.prompt}>
                              {sample.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedSample && originalPrompt && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={onResetToDefault}
                          disabled={prompt === originalPrompt}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          プロンプトの編集を戻す
                        </button>
                      )}
                    </div>

                    {/* テキストエリア */}
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="生成したい画像の説明を入力してください"
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                    />
                  </div>
                  <label className="form-label mb-2">生成対象の選択</label>
                </div>

                {/* 右カラム：参考画像設定 */}
                <div className="col-md-3">
                  {enableReferenceImage && (
                    <div className="ps-3">
                      <label className="form-label">デザインの参考画像</label>
                      {/* <p className="text-muted small mb-3">
                          この画像と似たテイストで生成します
                        </p> */}

                      {/* 固定高さのコンテンツエリア */}
                      <div className="d-flex flex-column">
                        {referenceImage ? (
                          <div className="text-center flex-1 d-flex flex-column justify-content-between w-100">
                            <div>
                              <div className="mb-2 d-flex justify-content-center">
                                <img
                                  src={getContentImageUrl(referenceImage.imageKey)}
                                  alt={referenceImage.name}
                                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                  className="rounded"
                                />
                              </div>
                              <h6 className="small text-truncate" title={referenceImage.name}>
                                {referenceImage.name}
                              </h6>
                              <p className="text-muted small mb-2">
                                {getImageType(referenceImage.name)} -
                                {formatTimeToDate(referenceImage.createdAt, 'yyyy/MM/dd')}
                              </p>
                            </div>
                            <div className="d-flex gap-1 w-100">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary flex-1"
                                onClick={onOpenImageGallery}
                              >
                                変更
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger flex-1"
                                onClick={onClearReferenceImage}
                              >
                                クリア
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-column justify-content-center h-100 gap-2 w-100 pt-5">
                            <button
                              type="button"
                              className="btn btn-common btn-outline-primary w-100"
                              onClick={onOpenImageGallery}
                            >
                              参考画像を選択
                            </button>
                            <button
                              type="button"
                              className="btn btn-common btn-outline-success w-100"
                              onClick={onOpenFileUploader}
                              disabled={processing}
                            >
                              {processing ? 'アップロード中...' : '新しい画像をアップロード'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 生成対象画像一覧 / 参考画像ギャラリー */}
            <div className="section-content mb-3">
              {/* 固定高さのヘッダー部分 */}
              <div style={{ height: '50px' }} className="mb-2">
                {showImageGallery ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="section-title mb-0">参考画像を選択</h5>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowImageGallery(false)}
                      >
                        生成対象画像に戻る
                      </button>
                    </div>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="ファイル名検索"
                      value={keywordState}
                      onChange={e => setKeywordState(e.target.value)}
                    />
                  </>
                ) : (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      {selectedImageKeys.size > 0 && (
                        <p className="text-muted small mb-0">
                          {selectedImageKeys.size}件の画像を生成対象に選択中
                        </p>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={onToggleAllImages}
                      >
                        {(() => {
                          const selectableKeys = props.imageItems
                            .filter(item => item.title && item.title.trim())
                            .map(item => item.key);
                          const allSelected = selectableKeys.every(key =>
                            selectedImageKeys.has(key)
                          );
                          return allSelected ? '全て解除' : '全て選択';
                        })()}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* 固定高さのコンテンツ部分 */}
              <div className="company-image-garrely">
                <div className="row row-0">
                  {showImageGallery
                    ? companyImagesState
                        .filter(image => keywordState === '' || image.name.includes(keywordState))
                        .map(image => (
                          <div
                            key={`reference-image-${image.id}`}
                            className="col-6 col-md-3 col-lg-2 mb-3"
                          >
                            <div
                              className="card card-media"
                              onClick={() => onSelectReferenceImage(image)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="card-body p-2">
                                <div className="img-container">
                                  <img
                                    src={getContentImageUrl(image.imageKey)}
                                    className="img-fluid d-block mx-auto"
                                    style={{ height: '80px', objectFit: 'cover' }}
                                  />
                                </div>
                                <h6 className="card-title small mt-2">{image.name}</h6>
                              </div>
                            </div>
                          </div>
                        ))
                    : props.imageItems.map(item => {
                        const generatedImage = generatedImages.find(img => img.key === item.key);
                        const hasExistingImage = !!item.imageKey;
                        const hasTitle = !!(item.title && item.title.trim());
                        const isSelected = selectedImageKeys.has(item.key);

                        return (
                          <div key={item.key} className="col-6 col-md-4 col-lg-3 mb-4">
                            <div
                              className={`card h-100 rounded-3 ${!isSelected ? 'opacity-50' : ''} ${
                                hasTitle ? 'cursor-pointer' : 'cursor-not-allowed'
                              }`}
                              onClick={() => hasTitle && onToggleImageSelection(item.key)}
                              style={{
                                cursor: hasTitle ? 'pointer' : 'not-allowed',
                                border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                                minHeight: '300px',
                                margin: '4px',
                              }}
                            >
                              <div className="card-body d-flex flex-column text-center p-3">
                                {/* 選択状態インジケーター */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  {!hasTitle && (
                                    <span className="text-danger small fw-bold">
                                      タイトル未入力
                                    </span>
                                  )}
                                  <div
                                    className={`badge ${
                                      !hasTitle
                                        ? 'bg-danger'
                                        : isSelected
                                          ? 'bg-primary'
                                          : 'bg-secondary'
                                    }`}
                                  >
                                    {!hasTitle ? '選択不可' : isSelected ? '選択中' : '未選択'}
                                  </div>
                                </div>

                                {/* 画像エリア - 固定高さ */}
                                <div
                                  className="img-container mb-3"
                                  style={{ height: '140px', overflow: 'hidden' }}
                                >
                                  {generatedImage ? (
                                    <img
                                      src={generatedImage.url}
                                      className="img-fluid d-block mx-auto rounded"
                                      style={{
                                        height: '140px',
                                        objectFit: 'contain',
                                        width: '100%',
                                        backgroundColor: '#f8f9fa',
                                      }}
                                      alt={item.title}
                                    />
                                  ) : hasExistingImage ? (
                                    <img
                                      src={getContentImageUrl(item.imageKey!)}
                                      className="img-fluid d-block mx-auto rounded"
                                      style={{
                                        height: '140px',
                                        objectFit: 'contain',
                                        width: '100%',
                                        backgroundColor: '#f8f9fa',
                                      }}
                                      alt={item.title}
                                    />
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center bg-light rounded"
                                      style={{ height: '140px', width: '100%' }}
                                    >
                                      <span className="text-muted small">No Image</span>
                                    </div>
                                  )}
                                </div>

                                {/* コンテンツエリア - フレックスで残りスペースを占有 */}
                                <div className="flex-grow-1 d-flex flex-column">
                                  <h6
                                    className="card-title mb-2"
                                    style={{ fontSize: '0.9rem', fontWeight: '600' }}
                                  >
                                    {item.name}
                                  </h6>

                                  {/* タイトル表示 - 固定高さでテキストオーバーフロー対応 */}
                                  <div
                                    className="mb-2"
                                    style={{ height: '40px', overflow: 'hidden' }}
                                  >
                                    <p
                                      className="text-muted small mb-0"
                                      style={{
                                        fontSize: '0.75rem',
                                        lineHeight: '1.2',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                      title={item.title || 'タイトル未入力'}
                                    >
                                      {item.title || 'タイトル未入力'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                </div>
              </div>
            </div>
            {/* AI画像一括生成ボタンとエラーメッセージ */}
            <div className="mb-3">
              <div className="text-center mb-2">
                <button
                  type="button"
                  className="btn btn-common moderate-blue btn-lg"
                  onClick={onGenerateImages}
                  disabled={generating}
                >
                  {generating ? '生成中...' : 'AIで画像を一括生成'}
                </button>
              </div>
              {errorMessage && (
                <div className="text-center">
                  <p className="text-danger small mb-0">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button onClick={onClose} type="button" className="btn btn-card btn-card-lg gray">
              キャンセル
            </button>
            <button
              onClick={onComplete}
              type="button"
              className="btn btn-card btn-card-lg moderate-blue"
              disabled={generatedImages.length === 0}
            >
              この画像で確定
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
}
